import { useEffect, useState } from "react";
import {
  reactQueryGet,
  reactQueryPut,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

export interface AgentActiveCheckResponse {
  enabled: boolean;
  id: number;
  name: string;
}

export const enableAgents = async (agents: AgentActiveCheckResponse[]) =>
  Promise.all(
    agents.map((agent: AgentActiveCheckResponse) =>
      reactQueryPut(internalApi.communityBot.agents.update({ id: agent.id }), {
        community_bot_profile: { enabled: true },
      }),
    ),
  );

export const getAgentEnabledStatusInActions = async (
  actions: any[],
): Promise<{
  disabledAgents: AgentActiveCheckResponse[];
}> => {
  const agentIds = actions
    .map((action: any) => action?.api_params?.community_bot_profile_id)
    .filter(Boolean);

  if (agentIds.length === 0) {
    return { disabledAgents: [] };
  }

  try {
    const activeAgents = await reactQueryGet(
      internalApi.communityBot.agents.activeCheck({ ids: agentIds }),
    );

    const disabledAgents =
      activeAgents?.filter(
        (agent: AgentActiveCheckResponse) => !agent.enabled,
      ) || [];

    return { disabledAgents };
  } catch {
    return { disabledAgents: [] };
  }
};

export const useAgentEnabledStatusInActions = (actions: any[]) => {
  const [disabledAgents, setDisabledAgents] = useState<
    AgentActiveCheckResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchDisabledAgents = async () => {
      setIsLoading(true);
      try {
        const { disabledAgents: agents } =
          await getAgentEnabledStatusInActions(actions);
        setDisabledAgents(agents);
      } catch {
        setDisabledAgents([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (actions && actions.length > 0) {
      void fetchDisabledAgents();
    } else {
      setDisabledAgents([]);
      setIsLoading(false);
    }
  }, [actions]);

  return { disabledAgents, isLoading };
};
